import { Checkbox, Input, Modal, Tooltip } from 'antd';
import { Icon } from 'components/common';
import { useAuth } from 'hooks';
import { deburr } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions as actions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { useLicenseFilteredColumns } from '../hooks/useLicenseFilteredColumns';
import { save as saveStrings, columnEditor as strings } from '../strings';
import {
  ExportTemplateButton,
  ModalFooter,
  ModalList,
  ModalListRow,
  SmallButton,
} from '../styled';
import { ColumnNameInput } from './ColumnNameInput';
import { DragHandle } from './DragHandle';
import { ExportTemplateModalHeader } from './ExportTemplateModalHeader';

const EMAIL_LICENCE_REQUIREMENT = 'ValidResearcherEmailLicence';

const subheadingStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '0px 20px 12px 20px',
  gap: 20,
};
const checkboxContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 42,
};

/**
 * Editor for columns within a single sheet.
 * Shows list of columns with toggles, rename, and reorder capabilities.
 * @param props
 * @param {Function} props.onConfirm - Callback when user confirms column edits.
 * @param {Function} props.onCancel - Callback when user cancels column edits.
 */
function ColumnEditor({ onConfirm, onCancel }) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const hideEmails = !!user?.hideResearcherEmail;

  const [searchText, setSearchText] = useState('');
  const [draggedId, setDraggedId] = useState(null);
  const lastDragKeyRef = useRef(null);

  const editingSheetId = useSelector(selectors.getDraftSheetId);
  const columnsMap = useSelector(selectors.getDraftSheetColumnMap);
  const columnOrder = useSelector(selectors.getDraftSheetColumnOrder);
  const draftDirty = useSelector(selectors.isDraftDirty);

  const handleBackPressed = () => {
    if (draftDirty) {
      Modal.confirm({
        bodyStyle: { padding: 15 },
        title: saveStrings.unsavedChangesTitle,
        content: saveStrings.unsavedChangesContent,
        okText: saveStrings.unsavedChangesOk,
        cancelText: saveStrings.unsavedChangesCancel,
        onOk: onCancel,
      });
      return;
    }
    onCancel();
  };

  const handleToggleColumn = columnId => {
    dispatch(
      actions.editingColumnEnableToggled({
        sheetId: editingSheetId,
        columnId,
      })
    );
  };

  // Calculate if we should show Enable All or Deselect All.
  // Unavailable columns are skipped: the select-all reducer cannot toggle
  // them, so they must not influence which button we show either.
  const nonMandatorySelectedCount = useMemo(() => {
    let count = 0;
    for (const col of Object.values(columnsMap)) {
      if (col.available === false) continue;
      if (col.enabled && !col.mandatory) {
        count++;
      }
    }
    return count;
  }, [columnsMap]);

  const handleDeselectAll = () => {
    dispatch(
      actions.editingSheetEnableAllSet({
        sheetId: editingSheetId,
        enabledValue: false,
      })
    );
  };

  const handleEnableAll = () => {
    dispatch(
      actions.editingSheetEnableAllSet({
        sheetId: editingSheetId,
        enabledValue: true,
      })
    );
  };

  const handleDragStart = (e, columnId) => {
    setDraggedId(columnId);
    lastDragKeyRef.current = null;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnId);
  };

  const handleDragOver = (e, targetColumnId) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetColumnId) return;

    const fromIndex = columnOrder.indexOf(draggedId);
    const toIndex = columnOrder.indexOf(targetColumnId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const dragKey = `${draggedId}:${targetColumnId}:${fromIndex}:${toIndex}`;
    if (lastDragKeyRef.current === dragKey) return;
    lastDragKeyRef.current = dragKey;

    const newOrder = [...columnOrder];
    const [movedId] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedId);

    dispatch(
      actions.editingSheetColumnOrderUpdated({
        sheetId: editingSheetId,
        newOrder,
      })
    );
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    lastDragKeyRef.current = null;
  };

  const { columns: licenseFilteredColumns } = useLicenseFilteredColumns(
    columnOrder,
    columnsMap
  );

  // Compute the rows we want to show, in the right order
  const allColumns = useMemo(() => {
    const searchLower = deburr(searchText.toLowerCase());

    return licenseFilteredColumns.filter(col => {
      if (!col) return false;
      if (!searchText) return true;

      if (deburr(col.defaultName.toLowerCase()).includes(searchLower)) {
        return true;
      }

      if (
        col.overridenName !== null &&
        deburr(col.overridenName.toLowerCase()).includes(searchLower)
      ) {
        return true;
      }
      return false;
    });
  }, [licenseFilteredColumns, searchText]);

  return (
    <div data-testid='et-modal-column-editor-view'>
      <ExportTemplateModalHeader onBackPressed={handleBackPressed} />
      <div style={subheadingStyle}>
        <Input
          placeholder={strings.searchPlaceholder}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          suffix={<Icon icon='keywords' size='1x' color='#000000D9' />}
          style={{ flex: 1 }}
          allowClear
        />
        {nonMandatorySelectedCount > 0 ? (
          <SmallButton onClick={handleDeselectAll}>
            {strings.deselectAll}
          </SmallButton>
        ) : (
          <SmallButton onClick={handleEnableAll}>
            {strings.enableAll}
          </SmallButton>
        )}
      </div>

      <ModalList>
        {allColumns.map(col => {
          // Email rows are locked (visible, checkbox disabled, state preserved)
          // when the user has the Hide Emails restriction. Consumer-side
          // EmailDataCleaner still blanks the value on export.
          const isEmailLocked =
            hideEmails &&
            (col.requirements || []).includes(EMAIL_LICENCE_REQUIREMENT);

          const tooltipTitle = isEmailLocked
            ? strings.emailLockedTooltip
            : col.mandatory
            ? 'Required column - cannot be removed'
            : undefined;

          return (
            <ModalListRow
              key={col.columnMetadataId}
              draggable={!isEmailLocked}
              onDragStart={e => handleDragStart(e, col.columnMetadataId)}
              onDragOver={e => handleDragOver(e, col.columnMetadataId)}
              onDragEnd={handleDragEnd}
              style={{
                opacity: draggedId === col.columnMetadataId ? 0.5 : 1,
              }}
            >
              <div style={checkboxContainerStyle}>
                <Tooltip title={tooltipTitle} placement='left'>
                  <Checkbox
                    checked={col.mandatory || col.enabled}
                    disabled={col.mandatory || isEmailLocked}
                    onChange={e =>
                      handleToggleColumn(col.columnMetadataId, e.target.checked)
                    }
                  />
                </Tooltip>
              </div>

              <ColumnNameInput
                sheetMetadataId={editingSheetId}
                columnMetadataId={col.columnMetadataId}
              />

              <DragHandle />
            </ModalListRow>
          );
        })}
      </ModalList>

      <ModalFooter>
        <ExportTemplateButton onClick={handleBackPressed}>
          Close
        </ExportTemplateButton>
        <ExportTemplateButton type='primary' onClick={onConfirm}>
          {strings.confirm}
        </ExportTemplateButton>
      </ModalFooter>
    </div>
  );
}

export default ColumnEditor;
